/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import { useState, useEffect } from 'react';
import classNames from 'classnames';
import { useField } from 'formik';
import { InputGroup, Form, Col } from 'react-bootstrap';
import { OutsideAlerter } from 'components';
import { useFirstRender } from 'hooks';
import { transformArrayWithId, getLabel } from 'util/transformHelpers';
import Label from '../Label';
import ErrorMessage from '../ErrorMessage';
import './styles.scss';

export default function Select({
  label = '',
  placeholder = '',
  groupClassName = 'my-2',
  selectContainerClassName = '',
  containerClassName = '',
  labelClassName = '',
  options = [],
  hasValidation = true,
  lastField = null,
  lastFieldIcon = null,
  onSelectLastField,
  children = null,
  onChange,
  defaultValue,
  showDocumentation='',
  eventIsInFuture='',
  variant = '',
  showLabel = true,
  ...props
}) {
  const [show, setShow] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState('');
  const [field, meta, helpers] = useField(props);

  const { error, touched } = meta;

  const isFirstRender = useFirstRender();

  const handleChange = (evt) => {
    const { value } = evt.target;

    if (onChange) onChange(evt, field);

    if (value === lastField && onSelectLastField) {
      onSelectLastField(evt);
      return;
    }

    helpers.setValue(value);
  };

  const handleInputChange = () => helpers.setValue(field.value);

  const handleClick = () => setShow((currentShow) => !currentShow);

  const handleClickOutsideAlerter = () => {
    if (show) {
      setShow(false);
    }
  };

  const handleAddValue = (value, selectedOption, isLastField = false) => {
    setSelectedLabel(isLastField ? selectedOption : getLabel(selectedOption));
    handleChange({ target: { value } });
  };

  const handleSelectLastField = () =>
    handleAddValue(lastField, lastField, true);

  useEffect(() => {
    if (field.value) {
      const selectedOption = options.find(
        ({ id, value }) => (id || value) === field.value
      );

      if (selectedOption) {
        setSelectedLabel(getLabel(selectedOption));
      }
    } else {
      setSelectedLabel('');
    }
  }, [field.value, options]);

  useEffect(() => {
    if (defaultValue && isFirstRender) {
      const selectedOption = options.find(
        ({ value }) => value === defaultValue
      );

      if (selectedOption) {
        helpers.setValue(selectedOption.value);
        setSelectedLabel(getLabel(selectedOption));
      }
    }
  }, [defaultValue, options, isFirstRender]);

  useEffect(() => {
    const existChildren = children && typeof children === 'function';

    if (existChildren && onChange) {
      onChange({ target: field });
    }
  }, [field.value, field.name]);

  const showClassName = show ? 'open' : 'close';

  const selectIconClassName = !error ? `Select__Input--${showClassName}` : '';

  const fieldValue = selectedLabel || field.value;

  const fieldPlaceholder = placeholder || label;

  const isPrimary = variant === 'primary';

  return (
    <InputGroup
      className={`Select Input ${groupClassName}`}
      hasValidation={hasValidation}
    >
      {showLabel && <Label label={label} className={labelClassName} />}

      <Col
        xs={12}
        className={`${containerClassName} ${selectContainerClassName}`}
      >
        <OutsideAlerter onClick={handleClickOutsideAlerter}>
          {children ? (
            <>
              {children({
                ...props,
                ...field,
                onClick: handleClick,
                value: fieldValue,
                placeholder: fieldPlaceholder,
                autoComplete: 'off',
                onChange: handleInputChange,
                fieldPlaceholder,
              })}
            </>
          ) : (
            <Form.Control
              {...props}
              {...field}
              value={fieldValue}
              onClick={handleClick}
              onKeyDown={handleClick}
              onKeyPress={handleClick}
              onChange={handleInputChange}
              placeholder={fieldPlaceholder}
              className={
                isPrimary
                  ? `btn btn-primary btn-flat Select__Input--primary--${showClassName}`
                  : `Select__Input has-feedback ${selectIconClassName}`
              }
              type="input"
              autoComplete="off"
              isInvalid={touched && error}
              readOnly
            />
          )}

          <div
            className={`${show ? 'dropdown open' : 'dropdown'}`}
            onClick={handleClick}
            onKeyDown={handleClick}
            tabIndex="0"
            role="button"
          >
            <ul className={classNames(isPrimary && 'primary')}>
              {options.map((option) => {
                const {
                  id,
                  value,
                  label: optionLabel = '',
                  labels = [],
                  verify
                } = option;
                const optionLabelClassName = `h6 p-3 ${
                  labels.length > 0
                    ? 'd-flex justify-content-around m-0'
                    : 'm-auto'
                }`;
                const handleSelect = () => handleAddValue(value, option);
                if ((verify && showDocumentation && !eventIsInFuture) || !verify){

                  return (
                    <li
                      key={id || value}
                      onClick={handleSelect}
                      onKeyDown={handleSelect}
                      onKeyPress={handleSelect}
                      className={classNames(isPrimary && 'primary')}
                    >
                      <div className={optionLabelClassName}>
                        {labels.length > 0 ? (
                          <>
                            {transformArrayWithId(labels).map((currentLabel) => {
                              const labelWidth = `${100 / labels.length}%`;
                              const textWeight = currentLabel.weight
                                ? `font-weight-${currentLabel.weight}`
                                : '';
                              const textPosition = currentLabel.position
                                ? `text-${currentLabel.position}`
                                : '';
                              const textClass = `m-0 ${textPosition} ${textWeight}`;

                              return (
                                <div
                                  key={currentLabel.id}
                                  style={{ width: labelWidth }}
                                >
                                  <p className={textClass}>
                                    {currentLabel.label}
                                  </p>
                                </div>
                              );
                            })}
                          </>
                        ) : (
                          optionLabel
                        )}
                      </div>
                    </li>
                  );
                        
                }
              })}

              {lastField && (
                <li
                  onClick={handleSelectLastField}
                  onKeyDown={handleSelectLastField}
                  onKeyPress={handleSelectLastField}
                >
                  <div className="h6 p-3 m-auto LastField">
                    {lastFieldIcon}
                    {lastField}
                  </div>
                </li>
              )}
            </ul>
          </div>
        </OutsideAlerter>
      </Col>

      <ErrorMessage {...meta} />
    </InputGroup>
  );
}
